<template>
  <b-modal
    :id="modalId"
    size="xl"
    centered
    scrollable
    hide-footer
    content-class="pl-modal sb-modal-content"
    dialog-class="pl-dialog"
    @hidden="$emit('closed')"
  >
    <template #modal-title>
      <div class="sb-title">
        <span class="sb-title-icon"><b-icon icon="boxes"></b-icon></span>
        <div>
          <div class="sb-title-main">Stock Breakdown</div>
          <div class="sb-title-sub">
            {{ productSku }}
            <span v-if="productName"> — {{ productName }}</span>
          </div>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading stock breakdown…</p>
    </div>

    <div v-else-if="hasData">
      <b-tabs v-model="activeTab" card class="sb-tabs">
        <!-- IN STOCK -->
        <b-tab title="In Stock" :title-link-class="'sb-tab-link is-instock'">
          <!-- ... (Existing In Stock content remains unchanged) ... -->
          <div class="sb-tab-toolbar">
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.inStock.search" placeholder="Filter by location or sublocation…"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <span class="sb-count">{{ filteredInStock.length }} rows · {{ sum(filteredInStock) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Sublocation</th>
                  <th class="is-num">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredInStock" :key="'is-' + i">
                  <td>{{ r.location_name }}</td>
                  <td>
                    <span class="sb-tag" :class="'is-' + r.location_type">
                      {{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}
                    </span>
                  </td>
                  <td>{{ r.sublocation_name || '—' }}</td>
                  <td class="is-num"><b>{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredInStock.length === 0">
                  <td colspan="4" class="sb-empty">No stock found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- SOLD -->
        <b-tab title="Sold" :title-link-class="'sb-tab-link is-sold'">
          <!-- ... (Existing Sold content remains unchanged) ... -->
          <div class="sb-tab-toolbar">
            <b-form-select v-model="filters.sold.location_id" :options="locationOptions" size="sm" class="sb-filter"></b-form-select>
            <b-form-select v-model="filters.sold.username" :options="invoiceUserOptions" size="sm" class="sb-filter"></b-form-select>
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.sold.invoice_id" placeholder="Invoice #"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <date-picker v-model="soldDateRange" range format="MMM DD, YYYY" value-type="format" :clearable="true" :editable="false" :append-to-body="false" :popup-style="{ zIndex: 3000 }" placeholder="All dates" class="sb-daterange-picker" @change="onSoldDateChange"></date-picker>
            <span class="sb-count">{{ filteredSold.length }} rows · {{ sum(filteredSold) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table sb-table--invoice">
              <thead>
                <tr>
                  <th class="col-id">Invoice #</th>
                  <th class="col-date">Date</th>
                  <th class="col-cust">Customer</th>
                  <th class="col-user">Sold by</th>
                  <th class="col-state">State</th>
                  <th class="col-loc">Location</th>
                  <th class="col-type">Type</th>
                  <th class="col-sub">Sublocation</th>
                  <th class="col-qty is-num">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredSold" :key="'sold-' + i" class="sb-invoice-row" @click="navigateToInvoice(r.invoice_id)" title="Click to view invoice">
                  <td class="col-id"><span class="sb-id">{{ r.invoice_id }}</span></td>
                  <td class="col-date">{{ fmtDate(r.date) }}</td>
                  <td class="col-cust">{{ r.customer || '—' }}</td>
                  <td class="col-user">{{ r.username || '—' }}</td>
                  <td class="col-state"><span class="sb-tag">{{ stateLabel(r.state) }}</span></td>
                  <td class="col-loc">{{ r.location_name }}</td>
                  <td class="col-type">
                    <span class="sb-tag" :class="'is-' + r.location_type">{{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}</span>
                  </td>
                  <td class="col-sub">{{ r.sublocation_name || '—' }}</td>
                  <td class="col-qty is-num"><b>{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredSold.length === 0">
                  <td colspan="9" class="sb-empty">No sold invoices found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- RESERVED -->
        <b-tab title="Reserved" :title-link-class="'sb-tab-link is-reserved'">
          <!-- ... (Existing Reserved content remains unchanged) ... -->
          <div class="sb-tab-toolbar">
            <b-form-select v-model="filters.reserved.location_id" :options="locationOptions" size="sm" class="sb-filter"></b-form-select>
            <b-form-select v-model="filters.reserved.username" :options="invoiceUserOptions" size="sm" class="sb-filter"></b-form-select>
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.reserved.invoice_id" placeholder="Invoice #"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <date-picker v-model="reservedDateRange" range format="MMM DD, YYYY" value-type="format" :clearable="true" :editable="false" :append-to-body="false" :popup-style="{ zIndex: 3000 }" placeholder="All dates" class="sb-daterange-picker" @change="onReservedDateChange"></date-picker>
            <span class="sb-count">{{ filteredReserved.length }} rows · {{ sum(filteredReserved) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table sb-table--invoice">
              <thead>
                <tr>
                  <th class="col-id">Invoice #</th>
                  <th class="col-date">Date</th>
                  <th class="col-cust">Customer</th>
                  <th class="col-user">Sold by</th>
                  <th class="col-state">State</th>
                  <th class="col-loc">Location</th>
                  <th class="col-type">Type</th>
                  <th class="col-sub">Sublocation</th>
                  <th class="col-qty is-num">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredReserved" :key="'res-' + i" class="sb-invoice-row" @click="navigateToInvoice(r.invoice_id)" title="Click to view invoice">
                  <td class="col-id"><span class="sb-id">{{ r.invoice_id }}</span></td>
                  <td class="col-date">{{ fmtDate(r.date) }}</td>
                  <td class="col-cust">{{ r.customer || '—' }}</td>
                  <td class="col-user">{{ r.username || '—' }}</td>
                  <td class="col-state"><span class="sb-tag">{{ stateLabel(r.state) }}</span></td>
                  <td class="col-loc">{{ r.location_name }}</td>
                  <td class="col-type">
                    <span class="sb-tag" :class="'is-' + r.location_type">{{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}</span>
                  </td>
                  <td class="col-sub">{{ r.sublocation_name || '—' }}</td>
                  <td class="col-qty is-num"><b>{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredReserved.length === 0">
                  <td colspan="9" class="sb-empty">No reserved invoices found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- LAYAWAY -->
        <b-tab title="Layaway" :title-link-class="'sb-tab-link is-layaway'">
          <!-- ... (Existing Layaway content remains unchanged) ... -->
          <div class="sb-tab-toolbar">
            <b-form-select v-model="filters.layaway.location_id" :options="locationOptions" size="sm" class="sb-filter"></b-form-select>
            <b-form-select v-model="filters.layaway.username" :options="invoiceUserOptions" size="sm" class="sb-filter"></b-form-select>
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.layaway.invoice_id" placeholder="Invoice #"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <date-picker v-model="layawayDateRange" range format="MMM DD, YYYY" value-type="format" :clearable="true" :editable="false" :append-to-body="false" :popup-style="{ zIndex: 3000 }" placeholder="All dates" class="sb-daterange-picker" @change="onLayawayDateChange"></date-picker>
            <span class="sb-count">{{ filteredLayaway.length }} rows · {{ sum(filteredLayaway) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table sb-table--invoice">
              <thead>
                <tr>
                  <th class="col-id">Invoice #</th>
                  <th class="col-date">Date</th>
                  <th class="col-cust">Customer</th>
                  <th class="col-user">Sold by</th>
                  <th class="col-state">State</th>
                  <th class="col-loc">Location</th>
                  <th class="col-type">Type</th>
                  <th class="col-sub">Sublocation</th>
                  <th class="col-qty is-num">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredLayaway" :key="'lay-' + i" class="sb-invoice-row" @click="navigateToInvoice(r.invoice_id)" title="Click to view invoice">
                  <td class="col-id"><span class="sb-id">{{ r.invoice_id }}</span></td>
                  <td class="col-date">{{ fmtDate(r.date) }}</td>
                  <td class="col-cust">{{ r.customer || '—' }}</td>
                  <td class="col-user">{{ r.username || '—' }}</td>
                  <td class="col-state"><span class="sb-tag">{{ stateLabel(r.state) }}</span></td>
                  <td class="col-loc">{{ r.location_name }}</td>
                  <td class="col-type">
                    <span class="sb-tag" :class="'is-' + r.location_type">{{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}</span>
                  </td>
                  <td class="col-sub">{{ r.sublocation_name || '—' }}</td>
                  <td class="col-qty is-num"><b>{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredLayaway.length === 0">
                  <td colspan="9" class="sb-empty">No layaway invoices found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- TRANSFER -->
        <b-tab title="Transfer" :title-link-class="'sb-tab-link is-transfer'">
          <!-- ... (Existing Transfer content remains unchanged) ... -->
          <div class="sb-tab-toolbar sb-tab-toolbar--transfer">
            <b-form-select v-model="filters.transfer.location_id" :options="locationOptions" size="sm" class="sb-filter"></b-form-select>
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.transfer.order_id" placeholder="Transfer order #"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <date-picker v-model="transferDateRange" range format="MMM DD, YYYY" value-type="format" :clearable="true" :editable="false" :append-to-body="false" :popup-style="{ zIndex: 3000 }" placeholder="All dates" class="sb-daterange-picker" @change="onTransferDateChange"></date-picker>
            <span class="sb-count">{{ filteredTransfer.length }} rows · {{ sum(filteredTransfer) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table sb-table--transfer">
              <thead>
                <tr>
                  <th>Transfer #</th>
                  <th>Date</th>
                  <th>Created by</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Sublocation</th>
                  <th class="is-num">Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredTransfer" :key="'tr-' + i">
                  <td><span class="sb-id">{{ r.transfer_order_id }}</span></td>
                  <td>{{ fmtDate(r.date) }}</td>
                  <td>{{ r.created_by || '—' }}</td>
                  <td>{{ r.location_name }}</td>
                  <td>
                    <span class="sb-tag" :class="'is-' + r.location_type">{{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}</span>
                  </td>
                  <td>{{ r.sublocation_name || '—' }}</td>
                  <td class="is-num"><b>{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredTransfer.length === 0">
                  <td colspan="7" class="sb-empty">No transfers found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- AVAILABLE -->
        <b-tab title="Available" :title-link-class="'sb-tab-link is-available'">
          <!-- ... (Existing Available content remains unchanged) ... -->
          <div class="sb-tab-toolbar">
            <b-input-group size="sm" class="sb-search">
              <b-form-input v-model="filters.available.search" placeholder="Filter by location or sublocation…"></b-form-input>
              <b-input-group-append><b-icon icon="search"></b-icon></b-input-group-append>
            </b-input-group>
            <span class="sb-count">{{ filteredAvailable.length }} rows · {{ sum(filteredAvailable) }} units</span>
          </div>
          <div class="sb-table-wrap">
            <table class="sb-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Sublocation</th>
                  <th class="is-num">Available</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredAvailable" :key="'av-' + i">
                  <td>{{ r.location_name }}</td>
                  <td>
                    <span class="sb-tag" :class="'is-' + r.location_type">{{ r.location_type === 'warehouse' ? 'Warehouse' : 'Store' }}</span>
                  </td>
                  <td>{{ r.sublocation_name || '—' }}</td>
                  <td class="is-num"><b class="is-good">{{ r.qty }}</b></td>
                </tr>
                <tr v-if="filteredAvailable.length === 0">
                  <td colspan="4" class="sb-empty">No available stock found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- NEW: HISTORY TAB -->
        <b-tab title="History" :title-link-class="'sb-tab-link is-history'">
          <div class="sb-tab-toolbar">
            <span class="sb-count">{{ historyData.warehouses.length + historyData.stores.length }} locations found</span>
          </div>
          
          <div class="sb-table-wrap" style="max-height: 500px; padding: 15px;">
            <!-- WAREHOUSES TABLE -->
            <div class="mb-4">
              <h6 class="sb-section-title">Warehouses</h6>
              <table class="sb-table sb-table--history">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="is-num">Tot Rcvd</th>
                    <th class="is-num">Transfer In</th>
                    <th class="is-num">Transfer Out</th>
                    <th class="is-num">Sold to Custs</th>
                    <th class="is-num">Sold to Stores</th>
                    <th class="is-num">Missing</th>
                    <th class="is-num">Available</th>
                    <th class="is-num">Damages</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in historyData.warehouses" :key="'wh-hist-' + idx">
                    <td class="fw-bold">{{ row.location_name }}</td>
                    <td class="is-num">{{ row.total_received || 0 }}</td>
                    <td class="is-num">{{ row.transfer_in || 0 }}</td>
                    <td class="is-num">{{ row.transfer_out || 0 }}</td>
                    <td class="is-num">{{ row.sold_to_customers || 0 }}</td>
                    <td class="is-num">{{ row.sold_to_stores || 0 }}</td>
                    <td class="is-num">{{ row.missing || '-' }}</td>
                    <td class="is-num"><b class="is-good">{{ row.available || 0 }}</b></td>
                    <td class="is-num text-danger">{{ row.damages || 0 }}</td>
                  </tr>
                  <!-- Warehouse Total -->
                  <tr v-if="historyData.warehouses.length" class="sb-total-row">
                    <td>TOTAL WH</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'total_received') }}</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'transfer_in') }}</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'transfer_out') }}</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'sold_to_customers') }}</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'sold_to_stores') }}</td>
                    <td class="is-num">-</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'available') }}</td>
                    <td class="is-num">{{ sumHistory('warehouses', 'damages') }}</td>
                  </tr>
                  <tr v-if="historyData.warehouses.length === 0">
                    <td colspan="9" class="sb-empty">No warehouse data found.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- STORES TABLE -->
            <div>
              <h6 class="sb-section-title">Stores</h6>
              <table class="sb-table sb-table--history">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="is-num">Tot Rcvd</th>
                    <th class="is-num">Purchased from WH</th>
                    <th class="is-num">Purchased from Store</th>
                    <th class="is-num">Sold to Stores</th>
                    <th class="is-num">Sold to Custs</th>
                    <th class="is-num">Missing</th>
                    <th class="is-num">Available</th>
                    <th class="is-num">Damages</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in historyData.stores" :key="'st-hist-' + idx">
                    <td class="fw-bold">{{ row.location_name }}</td>
                    <td class="is-num">{{ row.total_received || 0 }}</td>
                    <td class="is-num">{{ row.purchased_from_wh || 0 }}</td>
                    <td class="is-num">{{ row.purchased_from_store || 0 }}</td>
                    <td class="is-num">{{ row.sold_to_stores || 0 }}</td>
                    <td class="is-num">{{ row.sold_to_customers || 0 }}</td>
                    <td class="is-num">{{ row.missing || '-' }}</td>
                    <td class="is-num"><b class="is-good">{{ row.available || 0 }}</b></td>
                    <td class="is-num text-danger">{{ row.damages || 0 }}</td>
                  </tr>
                  <!-- Stores Total -->
                  <tr v-if="historyData.stores.length" class="sb-total-row">
                    <td>TOTAL STORES</td>
                    <td class="is-num">{{ sumHistory('stores', 'total_received') }}</td>
                    <td class="is-num">{{ sumHistory('stores', 'purchased_from_wh') }}</td>
                    <td class="is-num">{{ sumHistory('stores', 'purchased_from_store') }}</td>
                    <td class="is-num">{{ sumHistory('stores', 'sold_to_stores') }}</td>
                    <td class="is-num">{{ sumHistory('stores', 'sold_to_customers') }}</td>
                    <td class="is-num">-</td>
                    <td class="is-num">{{ sumHistory('stores', 'available') }}</td>
                    <td class="is-num">{{ sumHistory('stores', 'damages') }}</td>
                  </tr>
                  <tr v-if="historyData.stores.length === 0">
                    <td colspan="9" class="sb-empty">No store data found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </b-tab>

      </b-tabs>
    </div>

    <div v-else class="text-center py-5">
      <b-icon icon="exclamation-circle" font-scale="3" variant="warning"></b-icon>
      <p class="mt-3">Could not load stock breakdown.</p>
    </div>
  </b-modal>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";
import mappings from "@/utilities/mappings";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  name: "StockBreakdownModal",
  components: { DatePicker },
  props: {
    modalId: { type: String, default: "modal-stock-breakdown" },
    productSku: { type: String, default: "" },
    productName: { type: String, default: "" },
    locationId: { type: [Number, String], default: null },
    initialTab: { type: String, default: "in_stock" },
  },
  data() {
    return {
      loading: false,
      summary: null,
      inStockRows: [],
      soldRows: [],
      reservedRows: [],
      layawayRows: [],
      transferRows: [],
      availableRows: [],
      
      // NEW: History Data
      historyData: {
        warehouses: [],
        stores: []
      },

      tabsOrder: ["in_stock", "sold", "reserved", "layaway", "transfer", "available", "history"],
      activeTab: 0,

      // Range values for each tab
      soldDateRange: null,
      reservedDateRange: null,
      layawayDateRange: null,
      transferDateRange: null,

      filters: {
        inStock:   { search: "" },
        sold:      { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        reserved:  { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        layaway:   { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        transfer:  { location_id: null, order_id: "", date_from: null, date_to: null },
        available: { search: "" },
      },
    };
  },
  computed: {
    hasData() {
      return (
        this.inStockRows.length ||
        this.soldRows.length ||
        this.reservedRows.length ||
        this.layawayRows.length ||
        this.transferRows.length ||
        this.availableRows.length ||
        this.historyData.warehouses.length ||
        this.historyData.stores.length ||
        this.summary
      );
    },
    locationOptions() {
      // ... (Existing logic remains unchanged)
      const seen = {};
      const all = [
        ...this.inStockRows, ...this.soldRows, ...this.reservedRows,
        ...this.layawayRows, ...this.transferRows, ...this.availableRows
      ];
      const nameMap = {};
      all.forEach(r => {
        if (r.location_id) {
          seen[r.location_id] = true;
          if (r.location_name) nameMap[r.location_id] = r.location_name;
        }
      });
      const opts = Object.keys(seen).map(id => ({
        value: parseInt(id),
        text: nameMap[id] || `Location #${id}`
      }));
      return [{ value: null, text: 'ALL LOCATIONS' }, ...opts];
    },
    invoiceUserOptions() {
      // ... (Existing logic remains unchanged)
      const set = new Set();
      [...this.soldRows, ...this.reservedRows, ...this.layawayRows].forEach(r => {
        if (r.username) set.add(r.username);
      });
      return [{ value: null, text: 'ALL USERS' }, ...Array.from(set).map(u => ({ value: u, text: u }))];
    },
    filteredSold() {
      // ... (Existing logic remains unchanged)
      const f = this.filters.sold;
      let r = [...this.soldRows];
      if (f.location_id) r = r.filter(x => x.location_id === f.location_id);
      if (f.username)    r = r.filter(x => (x.username || "") === f.username);
      if (f.invoice_id)  r = r.filter(x => String(x.invoice_id).toLowerCase().includes(String(f.invoice_id).toLowerCase()));
      if (f.date_from)   r = r.filter(x => x.date && moment(x.date).isSameOrAfter(moment(f.date_from), "day"));
      if (f.date_to)     r = r.filter(x => x.date && moment(x.date).isSameOrBefore(moment(f.date_to), "day"));
      return r;
    },
    filteredReserved() {
      // ... (Existing logic remains unchanged)
      const f = this.filters.reserved;
      let r = [...this.reservedRows];
      if (f.location_id) r = r.filter(x => x.location_id === f.location_id);
      if (f.username)    r = r.filter(x => (x.username || "") === f.username);
      if (f.invoice_id)  r = r.filter(x => String(x.invoice_id).toLowerCase().includes(String(f.invoice_id).toLowerCase()));
      if (f.date_from)   r = r.filter(x => x.date && moment(x.date).isSameOrAfter(moment(f.date_from), "day"));
      if (f.date_to)     r = r.filter(x => x.date && moment(x.date).isSameOrBefore(moment(f.date_to), "day"));
      return r;
    },
    filteredLayaway() {
      // ... (Existing logic remains unchanged)
      const f = this.filters.layaway;
      let r = [...this.layawayRows];
      if (f.location_id) r = r.filter(x => x.location_id === f.location_id);
      if (f.username)    r = r.filter(x => (x.username || "") === f.username);
      if (f.invoice_id)  r = r.filter(x => String(x.invoice_id).toLowerCase().includes(String(f.invoice_id).toLowerCase()));
      if (f.date_from)   r = r.filter(x => x.date && moment(x.date).isSameOrAfter(moment(f.date_from), "day"));
      if (f.date_to)     r = r.filter(x => x.date && moment(x.date).isSameOrBefore(moment(f.date_to), "day"));
      return r;
    },
    filteredInStock() {
      // ... (Existing logic remains unchanged)
      const q = (this.filters.inStock.search || "").toLowerCase();
      let rows = [...this.inStockRows];
      rows.sort((a, b) => {
        const aIsWh = a.location_type === 'warehouse' ? 0 : 1;
        const bIsWh = b.location_type === 'warehouse' ? 0 : 1;
        if (aIsWh !== bIsWh) return aIsWh - bIsWh;
        return (a.location_name || '').localeCompare(b.location_name || '');
      });
      if (!q) return rows;
      return rows.filter(r =>
        (r.location_name || "").toLowerCase().includes(q) ||
        (r.sublocation_name || "").toLowerCase().includes(q)
      );
    },
    filteredTransfer() {
      // ... (Existing logic remains unchanged)
      const f = this.filters.transfer;
      let r = [...this.transferRows];
      if (f.location_id) r = r.filter(x => x.location_id === f.location_id);
      if (f.order_id)    r = r.filter(x => String(x.transfer_order_id).toLowerCase().includes(String(f.order_id).toLowerCase()));
      if (f.date_from)   r = r.filter(x => x.date && moment(x.date).isSameOrAfter(moment(f.date_from), "day"));
      if (f.date_to)     r = r.filter(x => x.date && moment(x.date).isSameOrBefore(moment(f.date_to), "day"));
      return r;
    },
    filteredAvailable() {
      // ... (Existing logic remains unchanged)
      const q = (this.filters.available.search || "").toLowerCase();
      let rows = [...this.availableRows];
      rows.sort((a, b) => {
        const aIsWh = a.location_type === 'warehouse' ? 0 : 1;
        const bIsWh = b.location_type === 'warehouse' ? 0 : 1;
        if (aIsWh !== bIsWh) return aIsWh - bIsWh;
        return (a.location_name || '').localeCompare(b.location_name || '');
      });
      if (!q) return rows;
      return rows.filter(r =>
        (r.location_name || "").toLowerCase().includes(q) ||
        (r.sublocation_name || "").toLowerCase().includes(q)
      );
    },
  },
  watch: {
    productSku() {
      if (this.productSku) this.load();
    },
    initialTab: {
      immediate: true,
      handler(tab) {
        const i = this.tabsOrder.indexOf(tab);
        if (i >= 0) this.activeTab = i;
      },
    },
  },
  methods: {
    fmtDate(d) { return d ? moment(d).format("MMM DD, YYYY HH:mm") : "—"; },
    sum(rows) { return rows.reduce((s, r) => s + (parseInt(r.qty) || 0), 0); },
    
    // NEW: Helper to sum history columns
    sumHistory(table, key) {
      return this.historyData[table].reduce((total, row) => total + (parseInt(row[key]) || 0), 0);
    },

    stateLabel(v) {
      const dict = mappings.states_dict || {};
      return dict[parseInt(v)] || `State ${v}`;
    },

    navigateToInvoice(invoiceId) {
      if (!invoiceId) return;
      const routeData = this.$router.resolve({ path: `/sales/viewsale/${invoiceId}/` });
      window.open(routeData.href, "_blank", "noopener,noreferrer");
    },

    applyRangeToFilter(range, filterObj) {
      if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
        filterObj.date_from = moment(range[0], "MMM DD, YYYY").format("YYYY-MM-DD");
        filterObj.date_to   = moment(range[1], "MMM DD, YYYY").format("YYYY-MM-DD");
      } else {
        filterObj.date_from = null;
        filterObj.date_to   = null;
      }
    },

    onSoldDateChange(range)     { this.applyRangeToFilter(range, this.filters.sold); },
    onReservedDateChange(range) { this.applyRangeToFilter(range, this.filters.reserved); },
    onLayawayDateChange(range)  { this.applyRangeToFilter(range, this.filters.layaway); },
    onTransferDateChange(range) { this.applyRangeToFilter(range, this.filters.transfer); },

    reset() {
      this.summary = null;
      this.inStockRows = []; this.soldRows = []; this.reservedRows = [];
      this.layawayRows = []; this.transferRows = []; this.availableRows = [];
      this.historyData = { warehouses: [], stores: [] }; // Reset history data
      this.soldDateRange = null;
      this.reservedDateRange = null;
      this.layawayDateRange = null;
      this.transferDateRange = null;
      this.filters = {
        inStock:   { search: "" },
        sold:      { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        reserved:  { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        layaway:   { location_id: null, username: null, invoice_id: "", date_from: null, date_to: null },
        transfer:  { location_id: null, order_id: "", date_from: null, date_to: null },
        available: { search: "" },
      };
    },
    async load() {
      if (!this.productSku) return;
      this.loading = true;
      this.reset();
      try {
        const data = { sku: this.productSku };
        if (this.locationId) data.location_id = this.locationId;

        // Fetch Main Breakdown Data
        const mainRes = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/product/get_product_stock_breakdown_details_v3/", data, this, resolve, reject);
        });

        if (mainRes.data && mainRes.data.result) {
          const r = mainRes.data.result;
          this.summary       = r.summary || null;
          this.inStockRows   = r.in_stock_rows   || [];
          this.soldRows      = r.sold_rows       || [];
          this.reservedRows  = r.reserved_rows   || [];
          this.layawayRows   = r.layaway_rows    || [];
          this.transferRows  = r.transfer_rows   || [];
          this.availableRows = r.available_rows  || [];
        }

        // Fetch History Data
        const historyRes = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/product/get_item_history_breakdown/", data, this, resolve, reject);
        });

        if (historyRes.data && historyRes.data.result) {
          this.historyData = historyRes.data.result;
        }

      } catch (e) {
        console.error("Error loading stock breakdown details:", e);
        this.$bvToast.toast("Error loading stock breakdown", { title: "Error", variant: "danger", autoHideDelay: 2500 });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<!-- =============================================================
     SCOPED STYLES
     ============================================================= -->
<style scoped>
/* ... (Existing styles remain unchanged) ... */
.sb-title { display: flex; align-items: center; gap: 12px; }
.sb-title-icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.22);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px;
}
.sb-title-main { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 700; }
.sb-title-sub { font-size: 12.5px; color: rgba(255, 255, 255, 0.7); font-family: 'Menlo', monospace; }

/* TABS */
.sb-tabs { margin-top: 4px; }
.sb-tabs >>> .nav-tabs {
  border-bottom: 2px solid #E5E1DA;
  gap: 4px;
  flex-wrap: wrap;
}
.sb-tabs >>> .nav-tabs .nav-link {
  border: 1.5px solid #E5E1DA;
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  font-weight: 700;
  font-size: 13px;
  color: #736C63;
  padding: 9px 18px;
  background: #F7F5F2;
}
.sb-tabs >>> .nav-tabs .nav-link.active { color: #fff; border-color: transparent; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-instock.active   { background: #221F1C; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-sold.active      { background: #B07A06; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-reserved.active  { background: #D4620F; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-layaway.active   { background: #8E4A8E; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-transfer.active  { background: #1F6690; }
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-available.active { background: #0F6E6B; }

/* NEW: History tab color */
.sb-tabs >>> .nav-tabs .nav-link.sb-tab-link.is-history.active   { background: #6B46C1; } 

.sb-tabs >>> .tab-content {
  padding: 0;
  border: 1.5px solid #E5E1DA;
  border-top: none;
  border-radius: 0 0 12px 12px;
  background: #fff;
}

/* TABLE (generic) */
.sb-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.sb-table thead th {
  position: sticky; top: 0;
  background: #F7F5F2;
  padding: 10px 12px;
  text-align: left;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #736C63;
  font-weight: 800;
  border-bottom: 1px solid #E5E1DA;
  z-index: 2;
  white-space: nowrap;
}
.sb-table thead th.is-num { text-align: right; }
.sb-table td {
  padding: 10px 12px;
  border-bottom: 1px dashed #EFEAE3;
  vertical-align: middle;
  color: #221F1C;
}
.sb-table td.is-num { text-align: right; font-variant-numeric: tabular-nums; }
.sb-table td.is-num b { font-family: 'Sora', sans-serif; font-weight: 800; }
.sb-table tbody tr:hover { background: #FAF9F7; }
.sb-table td b.is-good { color: #0F6E6B; }

.sb-id {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  font-weight: 700;
  color: #D4620F;
  background: #FFF1E4;
  padding: 2px 7px;
  border-radius: 5px;
}

.sb-tag {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 800;
  background: #EFECE7;
  color: #736C63;
  border: 1px solid #E5E1DA;
}
.sb-tag.is-warehouse { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.sb-tag.is-store     { background: #E2EFF7; color: #1F6690; border-color: #C3DCEC; }

.sb-empty {
  text-align: center;
  padding: 30px 16px;
  color: #A59D92;
  font-size: 13px;
}

/* NEW: History Table Styles */
.sb-section-title {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  color: #221F1C;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #E5E1DA;
}

.sb-table--history {
  min-width: 1000px; /* Prevents cramping on smaller screens */
}

.sb-table--history th,
.sb-table--history td {
  white-space: nowrap;
  padding: 8px 10px;
  font-size: 12.5px;
}

.sb-total-row {
  background-color: #F8F9FA;
  font-weight: 800;
  border-top: 2px solid #E5E1DA;
  border-bottom: 2px solid #E5E1DA;
}

.text-danger { color: #DC3545 !important; }
.fw-bold { font-weight: 700; }
</style>

<!-- =============================================================
     UNSCOPED STYLES
     ============================================================= -->
<style>
/* ... (Existing unscoped styles remain unchanged) ... */
.sb-modal-content .modal-body { padding: 14px 16px; }

.sb-tab-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #E5E1DA;
  background: #FCFBF9;
}
.sb-tab-toolbar--transfer { gap: 10px; }

.sb-filter {
  flex: 0 0 160px;
  width: 160px;
  max-width: 160px;
}
.sb-filter .custom-select,
.sb-filter .form-control {
  width: 100%;
  min-width: 0;
  height: 32px;
  font-size: 13px;
  padding: 2px 26px 2px 8px;
}

.sb-search {
  flex: 1 1 220px;
  min-width: 180px;
  max-width: 280px;
}
.sb-search .form-control { height: 32px; font-size: 13px; }

.sb-count {
  margin-left: auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #736C63;
  white-space: nowrap;
}

.sb-daterange-picker {
  flex: 0 0 auto;
}
.sb-daterange-picker .mx-input {
  height: 32px !important;
  padding: 0 34px 0 12px !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #221F1C !important;
  box-shadow: none !important;
  min-width: 240px;
  background: #fff;
}
.sb-daterange-picker .mx-input::placeholder { color: #A59D92; font-weight: 600; }
.sb-daterange-picker .mx-input:hover { border-color: #EE7A22 !important; }
.sb-daterange-picker .mx-input:focus {
  border-color: #EE7A22 !important;
  box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18) !important;
}
.sb-daterange-picker .mx-icon-calendar,
.sb-daterange-picker .mx-icon-clear {
  color: #EE7A22;
  top: 50%;
  transform: translateY(-50%);
}

.sb-daterange-picker .mx-datepicker-popup {
  border: 1px solid #E5E1DA !important;
  border-radius: 12px !important;
  box-shadow: 0 18px 40px -16px rgba(34, 31, 28, 0.45) !important;
  padding: 6px;
  z-index: 3000;
  width: auto !important;
  min-width: 540px !important;
}
.sb-daterange-picker .mx-calendar {
  display: flex !important;
  flex-direction: row !important;
  font-family: 'Manrope', system-ui, sans-serif;
}
.sb-daterange-picker .mx-calendar-content {
  width: 260px !important;
  padding: 0 !important;
}
.sb-daterange-picker .mx-calendar-content + .mx-calendar-content {
  border-left: 1px solid #E5E1DA !important;
}
.sb-daterange-picker .mx-calendar-header {
  padding: 6px 8px;
  background: #F7F5F2;
  border-bottom: 1px solid #E5E1DA;
}
.sb-daterange-picker .mx-calendar-header-label,
.sb-daterange-picker .mx-btn {
  color: #221F1C;
  font-weight: 700;
}
.sb-daterange-picker .mx-btn:hover { color: #EE7A22; }
.sb-daterange-picker .mx-table th {
  color: #736C63;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
}
.sb-daterange-picker .mx-table td .cell {
  color: #221F1C;
  font-weight: 600;
  border-radius: 6px;
}
.sb-daterange-picker .mx-table td .cell:hover {
  background: #FFF1E4;
  color: #D4620F;
}
.sb-daterange-picker .mx-table td.active .cell,
.sb-daterange-picker .mx-table td.in-range .cell {
  background: #EE7A22 !important;
  color: #fff !important;
}
.sb-daterange-picker .mx-table td.today .cell { color: #D4620F; font-weight: 800; }
.sb-daterange-picker .mx-table td.today.active .cell { color: #fff; }

.sb-table-wrap {
  max-height: 420px;
  overflow: auto;
  width: 100%;
}

.sb-table--invoice {
  width: 100%;
  min-width: 1180px;
  table-layout: auto;
}
.sb-table--invoice th,
.sb-table--invoice td { white-space: nowrap; }
.sb-table--invoice thead th { padding: 10px 14px; }
.sb-table--invoice .col-id    { width: 190px; }
.sb-table--invoice .col-date  { width: 180px; }
.sb-table--invoice .col-cust  { min-width: 150px; }
.sb-table--invoice .col-user  { min-width: 190px; }
.sb-table--invoice .col-state { width: 130px; }
.sb-table--invoice .col-loc   { min-width: 180px; }
.sb-table--invoice .col-type  { width: 130px; }
.sb-table--invoice .col-sub   { min-width: 150px; }
.sb-table--invoice .col-qty   { width: 80px; text-align: right; }
.sb-table--invoice thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #F7F5F2;
}

.sb-invoice-row { cursor: pointer; transition: background 0.12s ease; }
.sb-invoice-row:hover { background: #FFF1E4 !important; }
.sb-invoice-row:hover .sb-id { background: #EE7A22; color: #fff; }

.sb-table--transfer { min-width: 900px; }

.sb-table-wrap::-webkit-scrollbar { width: 8px; height: 8px; }
.sb-table-wrap::-webkit-scrollbar-thumb { background: #E5E1DA; border-radius: 99px; }
.sb-table-wrap::-webkit-scrollbar-thumb:hover { background: #C9C2B8; }
</style>


// WEBPACK FOOTER //
// src/components/Products/StockBreakdownModal.vue